import React from 'react';
import { Box, Button, Heading, Main, Page, PageContent, Text } from 'grommet';
import { redirect, useLoaderData, useRevalidator } from 'react-router-dom';
import {
    hasToken,
    API,
    MeetingService,
    UserService,
    AppConstants,
    EventLog,
    Utils,
    userIsCoach,
    userIsAdmin,
    buildInitialEventsQuery,
    useEventsInfinite,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { TasksTable } from './TasksTable';
import { ContactMeetings } from '@enrich/shared-lib';
import { buildInitialTodosQuery } from './useTodosInfinite';

export const Dashboard = () => {
    const {
        events = [],
        eventsPagination,
        allTodos = [],
        allTodosPagination,
        assignedTodos = [],
        assignedTodosPagination,
        assignableUsers = [],
        canAssign = false,
        userId,
        meetings = [],
        error,
    } = useLoaderData();
    const revalidator = useRevalidator();
    const { allEvents, loadMore, loading, hasMore } = useEventsInfinite(
        events,
        eventsPagination,
    );

    return (
        <Main>
            <Page
                kind="full"
                pad={{ top: '30px', bottom: '60px', horizontal: 'small' }}>
                {error && (
                    <PageContent>
                        <Box
                            pad="medium"
                            margin="small"
                            round="small"
                            border={{ color: 'enrich-red', size: '3px' }}>
                            <Text alignSelf="center">Error: {error}</Text>
                        </Box>
                    </PageContent>
                )}

                <PageContent>
                    <ContactMeetings meetings={meetings} />
                </PageContent>

                <PageContent>
                    <Heading level="3">Assigned Tasks</Heading>
                    <TasksTable
                        todos={assignedTodos}
                        pagination={assignedTodosPagination}
                        revalidator={revalidator}
                        assigneeId={userId}
                        canAssign={false}
                        canDelete={canAssign}
                        showContractClientFilter
                    />
                </PageContent>
                <PageContent>
                    <Heading level="3">All Tasks</Heading>
                    <TasksTable
                        todos={allTodos}
                        pagination={allTodosPagination}
                        revalidator={revalidator}
                        canAssign={canAssign}
                        canDelete={canAssign}
                        assignableUsers={assignableUsers}
                        showAssignmentFilter
                        showContractClientFilter
                    />
                </PageContent>
                <PageContent>
                    <Heading level="3">Recent updates</Heading>
                    <Box
                        pad="medium"
                        gap="small"
                        elevation="large"
                        height={{ min: 'small', max: 'xxlarge' }}
                        overflow={{ vertical: 'auto', horizontal: 'auto' }}
                        fill={true}>
                        {allEvents.map((event) => (
                            <EventLog key={event.id} event={event} />
                        ))}
                    </Box>
                    {hasMore && (
                        <Box align="center" margin={{ top: 'small' }}>
                            <Button
                                label={loading ? 'Loading…' : 'Load more'}
                                disabled={loading}
                                onClick={loadMore}
                            />
                        </Box>
                    )}
                    {!hasMore && allEvents.length > 0 && (
                        <Box align="center" margin={{ top: 'small' }}>
                            <Text size="small" color="dark-4">
                                All {allEvents.length} updates loaded
                            </Text>
                        </Box>
                    )}
                </PageContent>
            </Page>
        </Main>
    );
};

export async function loader() {
    if (!hasToken()) {
        return redirect('/login');
    }

    const userId = localStorage.getItem(AppConstants.cacheKeys.userId);
    let coachId = localStorage.getItem(AppConstants.cacheKeys.coachId);

    try {
        const user = userId ? await new UserService().getUser(userId) : null;

        if (!coachId && user?.coach?.id) {
            coachId = user.coach.id;
            localStorage.setItem(AppConstants.cacheKeys.coachId, coachId);
        }

        const canAssign = userIsCoach(user) || userIsAdmin(user);

        const allTodosResponse = await API.get(
            `/api/to-dos?${buildInitialTodosQuery()}`,
        );
        const assignedTodosResponse = await API.get(
            `/api/to-dos?${buildInitialTodosQuery({ assigneeId: userId })}`,
        );
        const eventsResponse = await API.get(
            `/api/coach-summary?${buildInitialEventsQuery()}`,
        );
        const meetings = coachId
            ? await new MeetingService().getCoachMeetings(coachId)
            : [];

        let assignableUsers = [];
        if (canAssign) {
            const usersQuery = QueryString.stringify(
                {
                    filters: {
                        $or: [
                            { role: { id: { $in: [3, 4, 5, 38] } } },
                            { role: { name: { $eqi: 'welcome call coach' } } },
                        ],
                    },
                    populate: ['role'],
                },
                { encodeValuesOnly: true },
            );
            const usersResponse = await API.get(`/api/users?${usersQuery}`);
            assignableUsers = usersResponse?.data ?? [];
        }

        return {
            events: Utils.deepFlatten(eventsResponse?.data.data),
            eventsPagination: eventsResponse?.data.meta?.pagination,
            allTodos: allTodosResponse?.data.data,
            allTodosPagination: allTodosResponse?.data.meta?.pagination,
            assignedTodos: assignedTodosResponse?.data.data,
            assignedTodosPagination:
                assignedTodosResponse?.data.meta?.pagination,
            assignableUsers,
            canAssign,
            userId,
            meetings,
        };
    } catch (error) {
        console.log(error);
        return {
            error: error.message,
        };
    }
}
